import { useState, useEffect, useCallback, useRef } from 'react';
import { bannerService } from '../services';
import type { Banner } from '../types';

interface UseBannerRotationOptions {
  /** Intervalo de rotação em milissegundos (padrão: 15 segundos) */
  rotationInterval?: number;
}

interface UseBannerRotationReturn {
  /** Banners normais (pool geral) */
  normalBanners: Banner[];
  /** Banners fullwidth */
  fullwidthBanners: Banner[];
  /** Se está carregando */
  loading: boolean;
  /** Pega N banners de uma lista com rodízio automático */
  pickFromPool: (list: Banner[], slotIndex: number, count?: number) => Banner[];
  /** Registra clique */
  trackClick: (bannerId: number) => void;
  /** Registra impressão */
  trackImpression: (bannerId: number) => void;
}

interface BannerCycleState {
  signature: string;
  order: number[];
  cursor: number;
}

const BANNER_CYCLE_KEY_PREFIX = 'vb_banner_cycle_v1';

const shuffleIds = (ids: number[]): number[] => {
  const shuffled = [...ids];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const getCycleSignature = (list: Banner[]): string => {
  return list
    .map((banner) => Number(banner.id))
    .filter((id) => Number.isFinite(id))
    .sort((a, b) => a - b)
    .join('-');
};

const createCycleState = (signature: string): BannerCycleState => {
  const ids = signature
    .split('-')
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id));

  return {
    signature,
    order: shuffleIds(ids),
    cursor: 0,
  };
};

const readCycleState = (signature: string): BannerCycleState => {
  if (typeof window === 'undefined' || !signature) return createCycleState(signature);

  const key = `${BANNER_CYCLE_KEY_PREFIX}:${signature}`;
  try {
    const stored = window.localStorage.getItem(key);
    if (!stored) return createCycleState(signature);

    const parsed = JSON.parse(stored) as BannerCycleState;
    const expectedIds = signature
      .split('-')
      .map((id) => Number(id))
      .sort((a, b) => a - b)
      .join('-');
    const storedIds = Array.isArray(parsed.order) ? [...parsed.order].sort((a, b) => a - b).join('-') : '';

    if (parsed.signature !== signature || storedIds !== expectedIds) {
      return createCycleState(signature);
    }

    return {
      signature,
      order: parsed.order,
      cursor: Number.isFinite(parsed.cursor) ? parsed.cursor : 0,
    };
  } catch {
    return createCycleState(signature);
  }
};

const writeCycleState = (state: BannerCycleState) => {
  if (typeof window === 'undefined' || !state.signature) return;

  try {
    window.localStorage.setItem(`${BANNER_CYCLE_KEY_PREFIX}:${state.signature}`, JSON.stringify(state));
  } catch {
    // Sem localStorage, o rodizio continua em memoria pela ordem retornada.
  }
};

/**
 * Hook para gerenciar banners com rodízio automático.
 *
 * Busca todos os banners públicos uma vez e deriva os pools exibidos na home.
 * Mantém uma fila embaralhada no navegador e avança a cada carga
 * da página, fazendo todos os banners aparecerem em ciclo.
 */
export function useBannerRotation(options: UseBannerRotationOptions = {}): UseBannerRotationReturn {
  const { rotationInterval = 15000 } = options;

  const [normalBanners, setNormalBanners] = useState<Banner[]>([]);
  const [fullwidthBanners, setFullwidthBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [rotationOffset, setRotationOffset] = useState(() => Math.floor(Math.random() * 100));
  const advancedCyclesRef = useRef<Set<string>>(new Set());

  // O endpoint público contém os mesmos pools que os endpoints por tipo usados
  // anteriormente. Mantemos o filtro no cliente para não disparar quatro
  // requisições equivalentes na carga inicial da home.
  const loadBanners = useCallback(async () => {
    try {
      setLoading(true);
      const banners = await bannerService.getActiveBanners();
      const home = banners.filter((banner) =>
        banner.type === 'home' || banner.type === 'normal' || banner.type === 'fullwidth' || !banner.type,
      );
      const vagas = banners.filter((banner) => banner.type === 'vagas');

      setNormalBanners(home);
      setFullwidthBanners(vagas);
    } catch (error) {
      console.error('Erro ao carregar banners:', error);
      setNormalBanners([]);
      setFullwidthBanners([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBanners();
  }, [loadBanners]);

  // Rotação automática
  useEffect(() => {
    const maxLen = Math.max(normalBanners.length, fullwidthBanners.length);
    if (maxLen <= 1) return;

    const interval = setInterval(() => {
      setRotationOffset((prev) => prev + 1);
    }, rotationInterval);

    return () => clearInterval(interval);
  }, [normalBanners.length, fullwidthBanners.length, rotationInterval]);

  /**
   * Pega N banners de uma lista com rodízio.
   * A ordem de cada pool e embaralhada uma vez e salva no navegador.
   * A cada carregamento da pagina o cursor avanca 1 posicao; durante
   * a permanencia na pagina, rotationOffset tambem movimenta os slots.
   */
  const pickFromPool = useCallback((list: Banner[], slotIndex: number, count: number = 2): Banner[] => {
    if (!list || list.length === 0) return [];
    const len = list.length;
    const signature = getCycleSignature(list);
    if (!signature) return list.slice(0, Math.min(count, len));

    const bannersById = new Map(list.map((banner) => [Number(banner.id), banner]));
    const cycle = readCycleState(signature);

    if (!advancedCyclesRef.current.has(signature)) {
      cycle.cursor = (cycle.cursor + 1) % len;
      advancedCyclesRef.current.add(signature);

      if (cycle.cursor === 0) {
        cycle.order = shuffleIds(cycle.order);
      }

      writeCycleState(cycle);
    }

    const orderedList = cycle.order
      .map((id) => bannersById.get(id))
      .filter((banner): banner is Banner => Boolean(banner));

    const sourceList = orderedList.length === len ? orderedList : list;
    const result: Banner[] = [];
    const usedIds = new Set<number>();
    const start = (cycle.cursor + slotIndex * count + rotationOffset) % len;

    for (let i = 0; i < Math.min(count, len); i++) {
      let idx = (start + i) % len;
      let attempts = 0;

      while (usedIds.has(sourceList[idx].id) && attempts < len) {
        idx = (idx + 1) % len;
        attempts++;
      }

      usedIds.add(sourceList[idx].id);
      result.push(sourceList[idx]);
    }

    return result;
  }, [rotationOffset]);

  const trackClick = useCallback((bannerId: number) => {
    bannerService.trackClick(bannerId).catch(console.error);
  }, []);

  const trackImpression = useCallback((bannerId: number) => {
    bannerService.trackImpression(bannerId).catch(console.error);
  }, []);

  return {
    normalBanners,
    fullwidthBanners,
    loading,
    pickFromPool,
    trackClick,
    trackImpression,
  };
}
